import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Minus, Square, X } from 'lucide-react';
import { useSimulation } from '../context/SimulationContext';

export interface WindowFrameProps {
  windowId: string;
  title: string;
  titleIcon?: React.ReactNode;
  defaultPosition: { x: number; y: number };
  defaultSize?: { w: number; h: number };
  minWidth?: number;
  minHeight?: number;
  children: React.ReactNode;
  className?: string;
  minimized: boolean;
  onMinimize: () => void;
  /** Optional `data-tutorial` on the window shell (e.g. command-prompt-window). */
  dataTutorialWindow?: string;
}

export function WindowFrame({
  windowId,
  title,
  titleIcon,
  defaultPosition,
  defaultSize = { w: 720, h: 480 },
  minWidth = 320,
  minHeight = 200,
  children,
  className = '',
  minimized,
  onMinimize,
  dataTutorialWindow,
}: WindowFrameProps): React.ReactElement {
  const { closeWindow, bringToFront, getZIndex, state } = useSimulation();
  const active = state.activeWindow === windowId;
  const [maximized, setMaximized] = useState(false);
  const [position, setPosition] = useState(defaultPosition);
  const [size, setSize] = useState(defaultSize);
  const preMaxRef = useRef({ position: defaultPosition, size: defaultSize });

  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);

  const onTitleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (maximized || minimized) return;
      const target = e.target as HTMLElement;
      if (
        target.closest('[data-window-control="true"]') ||
        target.closest('button')
      ) {
        return;
      }
      e.preventDefault();
      bringToFront(windowId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: position.x,
        origY: position.y,
      };
    },
    [bringToFront, windowId, maximized, minimized, position.x, position.y]
  );

  useEffect(() => {
    function onMove(e: MouseEvent) {
      const d = dragRef.current;
      if (!d || minimized || maximized) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      setPosition({
        x: Math.max(0, d.origX + dx),
        y: Math.max(0, d.origY + dy),
      });
    }

    function onUp() {
      dragRef.current = null;
    }

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [maximized, minimized]);

  useEffect(() => {
    if (!maximized) return;
    function apply() {
      setSize({
        w: Math.max(minWidth, window.innerWidth - 16),
        h: Math.max(minHeight, window.innerHeight - 72),
      });
    }
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [maximized, minWidth, minHeight]);

  const toggleMaximize = useCallback(() => {
    bringToFront(windowId);
    if (maximized) {
      setMaximized(false);
      setPosition(preMaxRef.current.position);
      setSize(preMaxRef.current.size);
      return;
    }
    preMaxRef.current = { position, size };
    setMaximized(true);
    setPosition({ x: 8, y: 8 });
    setSize({
      w: Math.max(minWidth, window.innerWidth - 16),
      h: Math.max(minHeight, window.innerHeight - 72),
    });
  }, [bringToFront, maximized, minHeight, minWidth, position, size, windowId]);

  const zStyle = getZIndex(windowId);
  const ring =
    active && !minimized ? 'ring-2 ring-blue-400/40' : '';

  const stylePos = maximized
    ? { left: 8, top: 8 }
    : { left: position.x, top: position.y };

  return (
    <div
      role="presentation"
      aria-hidden={minimized ? true : undefined}
      onMouseDown={() => {
        if (!minimized) bringToFront(windowId);
      }}
      className={`absolute flex flex-col rounded-xl overflow-hidden border border-white/20 bg-white/10 backdrop-blur-xl shadow-2xl shadow-black/40 animate-fade-in ${minimized ? 'hidden pointer-events-none' : ''} ${ring} ${className}`}
      style={{
        ...stylePos,
        width: maximized ? `calc(100% - 16px)` : size.w,
        height: maximized ? `calc(100% - 72px)` : size.h,
        minWidth,
        minHeight,
        zIndex: zStyle,
      }}
      data-window-id={windowId}
      {...(dataTutorialWindow
        ? { 'data-tutorial': dataTutorialWindow }
        : {})}
    >
      <header
        className="flex h-10 shrink-0 items-center gap-2 border-b border-white/10 bg-black/25 px-2 select-none cursor-default"
        onMouseDown={onTitleMouseDown}
      >
        <div className="flex shrink-0 items-center gap-1.5 px-2 min-w-0 flex-1">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white/10 text-sm [&>svg]:h-4 [&>svg]:w-4">
            {titleIcon}
          </span>
          <span className="truncate text-sm font-medium text-white/95">
            {title}
          </span>
        </div>
        <div
          className="flex items-center shrink-0"
          data-window-control="true"
          data-titlebar-controls
        >
          <button
            type="button"
            aria-label="Minimize"
            className="grid h-8 w-10 place-items-center text-white/80 hover:bg-white/10 transition-colors rounded"
            data-window-control="true"
            onClick={(e) => {
              e.stopPropagation();
              bringToFront(windowId);
              onMinimize();
            }}
          >
            <Minus className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Maximize"
            className="grid h-8 w-10 place-items-center text-white/80 hover:bg-white/10 transition-colors rounded"
            data-window-control="true"
            onClick={(e) => {
              e.stopPropagation();
              toggleMaximize();
            }}
          >
            <Square className="h-3.5 w-3.5" aria-hidden strokeWidth={2.25} />
          </button>
          <button
            type="button"
            aria-label="Close"
            className="grid h-8 w-12 place-items-center text-white hover:bg-red-500/85 transition-colors rounded rounded-tr-lg"
            data-window-control="true"
            onClick={(e) => {
              e.stopPropagation();
              closeWindow(windowId);
            }}
          >
            <X className="h-4 w-4" aria-hidden strokeWidth={2.25} />
          </button>
        </div>
      </header>
      <div className="relative flex flex-1 min-h-0 min-w-0 flex-col">
        {children}
      </div>
    </div>
  );
}
